<template>
  <div class="media-display">
    <!-- YouTube 影片 -->
    <template v-if="post.source == 'youtube'">
      <div class="bg-gradient-to-r from-wood-100 to-earth-100 rounded-xl p-4 mb-4">
        <div class="flex items-center space-x-2 mb-2">
          <i class="fas fa-play-circle text-wood-600"></i>
          <span class="text-sm text-wood-700">
            原文 {{ post.authur }} 張貼於 {{ post.source }} - {{ post.title }}
          </span>
        </div>
        <div 
          class='embed-container rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity duration-200 relative group' 
          @click="openYoutubeLightbox"
        >
          <iframe 
            width='560' 
            height='315' 
            :src="post.video" 
            frameborder='0' 
            allow='accelerometer; encrypted-media; gyroscope; picture-in-picture' 
            allowfullscreen>
          </iframe>
          <!-- 全螢幕提示 -->
          <div class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div class="bg-black/60 text-white px-3 py-2 rounded-full text-sm flex items-center space-x-2">
              <i class="fas fa-expand"></i>
              <span>全螢幕觀看</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- 小紅書影片 -->
    <template v-if="post.source == 'redbook'">
      <div class="bg-gradient-to-r from-wood-100 to-earth-100 rounded-xl p-4 mb-4">
        <div class="flex items-center space-x-2 mb-2">
          <i class="fas fa-video text-wood-600"></i>
          <span class="text-sm text-wood-700">
            原文 {{ post.authur }} 張貼於 {{ post.source }} - {{ post.title }}
          </span>
        </div>
        <div 
          :id="'mse-'+post.id" 
          class="rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity duration-200" 
          @click="openVideoLightbox"
        ></div>
      </div>
    </template>
    
    <!-- 一般圖片 -->
    <template v-if="post.images && post.images.length > 0 && post.source != 'redbook'">
      <div class="mb-4">
        <!-- 單張圖片 -->
        <div v-if="post.images.length === 1" class="cursor-pointer">
          <img
            :src="getImageUrl(post.images[0])"
            :alt="getImageAlt(post.images[0])"
            class="w-full rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
            @click="openLightbox(0)"
          />
        </div>
        <!-- 多張圖片輪播 -->
        <div v-else class="relative">
          <div class="overflow-hidden rounded-xl">
            <div class="flex transition-transform duration-300 ease-in-out" :style="{ transform: `translateX(-${currentImageIndex * 100}%)` }">
              <img
                v-for="(image, index) in post.images"
                :key="index"
                :src="getImageUrl(image)"
                :alt="getImageAlt(image)"
                class="w-full flex-shrink-0 shadow-md cursor-pointer hover:opacity-90 transition-opacity duration-200"
                @click="openLightbox(index)"
              />
            </div>
          </div>
          <!-- 導航按鈕 -->
          <button 
            v-if="post.images.length > 1"
            @click="previousImage"
            class="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-black/70 transition-colors"
          >
            <i class="fas fa-chevron-left text-sm"></i>
          </button>
          <button 
            v-if="post.images.length > 1"
            @click="nextImage"
            class="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-black/70 transition-colors"
          >
            <i class="fas fa-chevron-right text-sm"></i>
          </button>
          <!-- 圖片指示器 -->
          <div v-if="post.images.length > 1" class="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
            <button 
              v-for="(_, index) in post.images" 
              :key="index"
              @click="currentImageIndex = index"
              class="w-2 h-2 rounded-full transition-colors"
              :class="currentImageIndex === index ? 'bg-white' : 'bg-white/50'"
            ></button>
          </div>
          <!-- 圖片計數器 -->
          <div v-if="post.images.length > 1" class="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded-full text-xs">
            {{ currentImageIndex + 1 }} / {{ post.images.length }}
          </div>
        </div>
      </div>
    </template>
    
    <!-- 舊版單張圖片相容性 -->
    <template v-else-if="post.image && post.source != 'redbook'">
      <div class="mb-4 cursor-pointer">
        <img
          :src="getImageUrl(post.image)"
          :alt="getImageAlt(post.image)"
          class="w-full rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]"
          @click="openLightbox(0)"
        />
      </div>
    </template>

    <!-- 燈箱模態框 -->
    <Teleport to="body">
      <div 
        v-if="isLightboxOpen" 
        class="fixed inset-0 z-[9999] flex items-center justify-center"
        @click="closeLightbox"
      >
        <!-- 半透明深色背景 -->
        <div 
          class="absolute inset-0 bg-black transition-opacity duration-300"
          :class="isLightboxAnimating ? 'opacity-0' : 'opacity-90'"
        ></div>
        
        <!-- 燈箱內容 -->
        <div 
          class="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center transform transition-all duration-300"
          :class="isLightboxAnimating ? 'scale-75 opacity-0' : 'scale-100 opacity-100'"
          @click.stop
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd"
        >
          <!-- 主圖片 -->
          <img
            :src="getImageUrl(post.images ? post.images[lightboxImageIndex] : post.image)"
            :alt="getImageAlt(post.images ? post.images[lightboxImageIndex] : post.image)"
            :key="`lightbox-${lightboxImageIndex}`"
            class="lightbox-image max-w-[90vw] max-h-[90vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
            @click.stop
            @load="onImageLoad"
          />
          
          <!-- 關閉按鈕 -->
          <button 
            @click="closeLightbox"
            class="lightbox-button absolute top-4 right-4 bg-black/60 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-black/80"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
          
          <!-- 圖片指示器 (多張圖片時) -->
          <div 
            v-if="post.images && post.images.length > 1" 
            class="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex space-x-2"
          >
            <button 
              v-for="(_, index) in post.images" 
              :key="index"
              @click="lightboxImageIndex = index"
              class="w-3 h-3 rounded-full transition-all duration-200 hover:scale-125"
              :class="lightboxImageIndex === index ? 'bg-white shadow-lg' : 'bg-white/50 hover:bg-white/75'"
            ></button>
          </div>
          
          <!-- 圖片計數器 (多張圖片時) -->
          <div 
            v-if="post.images && post.images.length > 1" 
            class="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm font-medium"
          >
            {{ lightboxImageIndex + 1 }} / {{ post.images.length }}
          </div>
        </div>
        
        <!-- 左右導航按鈕 (多張圖片時) - 固定在瀏覽器兩側 -->
        <template v-if="(post.images && post.images.length > 1)">
          <button 
            @click.stop="previousLightboxImage"
            class="lightbox-button fixed left-8 top-1/2 transform -translate-y-1/2 bg-black/60 text-white rounded-full w-16 h-16 flex items-center justify-center hover:bg-black/80 z-[10001]"
          >
            <i class="fas fa-chevron-left text-xl"></i>
          </button>
          <button 
            @click.stop="nextLightboxImage"
            class="lightbox-button fixed right-8 top-1/2 transform -translate-y-1/2 bg-black/60 text-white rounded-full w-16 h-16 flex items-center justify-center hover:bg-black/80 z-[10001]"
          >
            <i class="fas fa-chevron-right text-xl"></i>
          </button>
        </template>
      </div>
    </Teleport>
    
    <!-- 影片燈箱模態框 -->
    <Teleport to="body">
      <div 
        v-if="isVideoLightboxOpen" 
        class="fixed inset-0 z-[9999] flex items-center justify-center"
        @click="closeVideoLightbox"
      >
        <!-- 半透明深色背景 -->
        <div 
          class="absolute inset-0 bg-black transition-opacity duration-300"
          :class="isVideoLightboxAnimating ? 'opacity-0' : 'opacity-95'"
        ></div>
        
        <!-- 影片燈箱內容 -->
        <div 
          class="relative max-w-[95vw] max-h-[95vh] w-full h-full flex items-center justify-center transform transition-all duration-300"
          :class="isVideoLightboxAnimating ? 'scale-75 opacity-0' : 'scale-100 opacity-100'"
          @click.stop
        >
          <!-- 影片播放器容器 -->
          <div 
            :id="'lightbox-mse-'+post.id" 
            class="lightbox-video max-w-[90vw] max-h-[90vh] w-auto h-auto rounded-lg shadow-2xl overflow-hidden bg-black"
            style="aspect-ratio: 9/16; width: min(90vw, calc(90vh * 9/16)); height: min(90vh, calc(90vw * 16/9));"
          ></div>
          
          <!-- 關閉按鈕 -->
          <button 
            @click="closeVideoLightbox"
            class="lightbox-button absolute top-4 right-4 bg-black/60 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-black/80 z-10"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
          
          <!-- 影片資訊 -->
          <div v-if="post.title" class="absolute bottom-4 left-4 bg-black/60 text-white px-4 py-2 rounded-lg max-w-md">
            <div class="flex items-center space-x-2 text-sm">
              <i class="fas fa-video"></i>
              <span>{{ post.authur }} • {{ post.source }}</span>
            </div>
            <div class="text-sm opacity-90 mt-1">{{ post.title }}</div>
          </div>
        </div>
      </div>
    </Teleport>
    
    <!-- YouTube 燈箱模態框 -->
    <Teleport to="body">
      <div 
        v-if="isYoutubeLightboxOpen" 
        class="fixed inset-0 z-[9999] flex items-center justify-center"
        @click="closeYoutubeLightbox"
      >
        <!-- 半透明深色背景 -->
        <div 
          class="absolute inset-0 bg-black transition-opacity duration-300"
          :class="isYoutubeLightboxAnimating ? 'opacity-0' : 'opacity-95'"
        ></div>
        
        <!-- YouTube 燈箱內容 -->
        <div 
          class="relative max-w-[95vw] max-h-[95vh] w-full h-full flex items-center justify-center transform transition-all duration-300"
          :class="isYoutubeLightboxAnimating ? 'scale-75 opacity-0' : 'scale-100 opacity-100'"
          @click.stop
        >
          <!-- YouTube 播放器容器 -->
          <div 
            :id="'youtube-lightbox-'+post.id" 
            class="lightbox-youtube max-w-[90vw] max-h-[90vh] w-auto h-auto rounded-lg shadow-2xl overflow-hidden bg-black"
            style="aspect-ratio: 16/9; width: min(90vw, calc(90vh * 16/9));"
          >
            <iframe 
              width="100%" 
              height="100%" 
              :src="post.video + '&autoplay=1'" 
              frameborder="0" 
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" 
              allowfullscreen>
            </iframe>
          </div>
          
          <!-- 關閉按鈕 -->
          <button 
            @click="closeYoutubeLightbox"
            class="lightbox-button absolute top-4 right-4 bg-black/60 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-black/80 z-10"
          >
            <i class="fas fa-times text-lg"></i>
          </button>
          
          <!-- 影片資訊 -->
          <div v-if="post.title" class="absolute bottom-4 left-4 bg-black/60 text-white px-4 py-2 rounded-lg max-w-md">
            <div class="flex items-center space-x-2 text-sm">
              <i class="fas fa-play-circle"></i>
              <span>{{ post.authur }} • {{ post.source }}</span>
            </div>
            <div class="text-sm opacity-90 mt-1">{{ post.title }}</div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { defineProps, ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import Player from 'xgplayer'

// 圖片輪播狀態
const currentImageIndex = ref(0)

// 燈箱狀態管理
const isLightboxOpen = ref(false)
const lightboxImageIndex = ref(0)
const isLightboxAnimating = ref(false)
const isImageTransitioning = ref(false)
const slideDirection = ref('') // 'left' or 'right'

// 影片燈箱狀態管理
const isVideoLightboxOpen = ref(false)
const isVideoLightboxAnimating = ref(false)

// YouTube 燈箱狀態管理
const isYoutubeLightboxOpen = ref(false)
const isYoutubeLightboxAnimating = ref(false)

// 小紅書影片播放器
const videoPlayer = ref(null)

// 觸控手勢相關
const touchStartX = ref(0)
const touchEndX = ref(0)

const props = defineProps({
  post: {
    type: Object,
    required: true
  }
})

// 處理圖片 URL 的輔助函式（支援新舊格式）
const getImageUrl = (image) => {
  if (typeof image === 'string') {
    // 舊格式：字串 URL
    return image
  } else if (image && image.url) {
    // 新格式：物件 { url, alt }
    return image.url
  } else {
    // 預設情況
    return ''
  }
}

// 處理圖片 Alt 文字的輔助函式（支援新舊格式）
const getImageAlt = (image) => {
  if (typeof image === 'string') {
    // 舊格式：字串 URL，使用預設 alt
    return '圖片'
  } else if (image && image.alt) {
    // 新格式：物件 { url, alt }
    return image.alt || '圖片'
  } else {
    // 預設情況
    return '圖片'
  }
}

// 圖片輪播控制
const nextImage = () => {
  if (props.post.images && props.post.images.length > 1) {
    currentImageIndex.value = (currentImageIndex.value + 1) % props.post.images.length
  }
}

const previousImage = () => {
  if (props.post.images && props.post.images.length > 1) {
    currentImageIndex.value = currentImageIndex.value === 0 ? props.post.images.length - 1 : currentImageIndex.value - 1
  }
}

// 燈箱相關方法
const openLightbox = (imageIndex) => {
  lightboxImageIndex.value = imageIndex
  isLightboxOpen.value = true
  isLightboxAnimating.value = true
  document.body.style.overflow = 'hidden' // 防止背景滾動
  
  nextTick(() => {
    // 為燈箱圖片添加進入動畫
    const lightboxImg = document.querySelector('.lightbox-image')
    if (lightboxImg) {
      lightboxImg.classList.add('zoom-in')
      lightboxImg.classList.remove('zoom-out')
    }
    
    setTimeout(() => {
      isLightboxAnimating.value = false
    }, 100)
  })
}

const closeLightbox = () => {
  isLightboxAnimating.value = true
  
  // 為燈箱圖片添加退出動畫
  const lightboxImg = document.querySelector('.lightbox-image')
  if (lightboxImg) {
    lightboxImg.classList.add('zoom-out')
    lightboxImg.classList.remove('zoom-in')
  }
  
  setTimeout(() => {
    isLightboxOpen.value = false
    isLightboxAnimating.value = false
    document.body.style.overflow = '' // 恢復滾動
    
    // 清理動畫類別
    if (lightboxImg) {
      lightboxImg.classList.remove('zoom-out')
    }
  }, 400) // 配合動畫時長
}

const nextLightboxImage = () => {
  if (props.post.images && props.post.images.length > 1 && !isImageTransitioning.value) {
    isImageTransitioning.value = true
    slideDirection.value = 'left'
    
    // 預載入下一張圖片
    const nextIndex = (lightboxImageIndex.value + 1) % props.post.images.length
    preloadImage(props.post.images[nextIndex])
    
    // 為當前圖片添加退出動畫
    const lightboxImg = document.querySelector('.lightbox-image')
    if (lightboxImg) {
      lightboxImg.classList.remove('slide-left', 'slide-right', 'slide-in-left', 'slide-in-right')
      lightboxImg.classList.add('slide-left')
    }
    
    setTimeout(() => {
      lightboxImageIndex.value = nextIndex
      
      nextTick(() => {
        // 為新圖片添加進入動畫
        const newLightboxImg = document.querySelector('.lightbox-image')
        if (newLightboxImg) {
          // 確保圖片已載入後再開始動畫
          if (newLightboxImg.complete) {
            startSlideInAnimation(newLightboxImg, 'slide-in-left')
          } else {
            // 等待圖片載入完成
            newLightboxImg.onload = () => {
              startSlideInAnimation(newLightboxImg, 'slide-in-left')
            }
          }
        }
      })
    }, 300)
  }
}

const previousLightboxImage = () => {
  if (props.post.images && props.post.images.length > 1 && !isImageTransitioning.value) {
    isImageTransitioning.value = true
    slideDirection.value = 'right'
    
    // 預載入上一張圖片
    const prevIndex = lightboxImageIndex.value === 0 ? props.post.images.length - 1 : lightboxImageIndex.value - 1
    preloadImage(props.post.images[prevIndex])
    
    // 為當前圖片添加退出動畫
    const lightboxImg = document.querySelector('.lightbox-image')
    if (lightboxImg) {
      lightboxImg.classList.remove('slide-left', 'slide-right', 'slide-in-left', 'slide-in-right')
      lightboxImg.classList.add('slide-right')
    }
    
    setTimeout(() => {
      lightboxImageIndex.value = prevIndex
      
      nextTick(() => {
        // 為新圖片添加進入動畫
        const newLightboxImg = document.querySelector('.lightbox-image')
        if (newLightboxImg) {
          // 確保圖片已載入後再開始動畫
          if (newLightboxImg.complete) {
            startSlideInAnimation(newLightboxImg, 'slide-in-right')
          } else {
            // 等待圖片載入完成
            newLightboxImg.onload = () => {
              startSlideInAnimation(newLightboxImg, 'slide-in-right')
            }
          }
        }
      })
    }, 300)
  }
}

// 圖片預載入輔助函數
const preloadImage = (image) => {
  const img = new Image()
  img.src = getImageUrl(image)
  return img
}

// 啟動滑入動畫的輔助函數
const startSlideInAnimation = (imgElement, animationClass) => {
  imgElement.classList.remove('slide-left', 'slide-right', 'slide-in-left', 'slide-in-right')
  // 使用 requestAnimationFrame 確保瀏覽器準備好進行動畫
  requestAnimationFrame(() => {
    imgElement.classList.add(animationClass)
    
    setTimeout(() => {
      imgElement.classList.remove(animationClass)
      isImageTransitioning.value = false
    }, 400)
  })
}

// 圖片載入完成回調
const onImageLoad = () => {
  // 圖片載入完成，確保佈局已穩定
}

// 鍵盤事件處理
const handleKeydown = (event) => {
  if (!isLightboxOpen.value && !isVideoLightboxOpen.value && !isYoutubeLightboxOpen.value) return
  
  switch (event.key) {
    case 'Escape':
      if (isLightboxOpen.value) {
        closeLightbox()
      } else if (isVideoLightboxOpen.value) {
        closeVideoLightbox()
      } else if (isYoutubeLightboxOpen.value) {
        closeYoutubeLightbox()
      }
      break
    case 'ArrowLeft':
      if (isLightboxOpen.value) {
        previousLightboxImage()
      }
      break
    case 'ArrowRight':
      if (isLightboxOpen.value) {
        nextLightboxImage()
      }
      break
  }
}

// 觸控手勢處理
const handleTouchStart = (event) => {
  touchStartX.value = event.touches[0].clientX
}

const handleTouchEnd = (event) => {
  touchEndX.value = event.changedTouches[0].clientX
  const diff = touchStartX.value - touchEndX.value
  const minSwipeDistance = 50
  
  if (Math.abs(diff) > minSwipeDistance) {
    if (diff > 0) {
      nextLightboxImage() // 向左滑動，下一張
    } else {
      previousLightboxImage() // 向右滑動，上一張
    }
  }
}

// 影片燈箱相關方法
const openVideoLightbox = () => {
  isVideoLightboxOpen.value = true
  isVideoLightboxAnimating.value = true
  document.body.style.overflow = 'hidden' // 防止背景滾動
  
  nextTick(() => {
    const originalVideoContainer = document.getElementById(`mse-${props.post.id}`)
    const lightboxVideoContainer = document.getElementById(`lightbox-mse-${props.post.id}`)
    
    if (originalVideoContainer && lightboxVideoContainer) {
      // 將原播放器容器移動到燈箱中（而不是複製）
      // 先暫存原位置資訊
      const originalParent = originalVideoContainer.parentElement
      
      // 在原位置放置一個占位元素
      const placeholder = document.createElement('div')
      placeholder.id = `video-placeholder-${props.post.id}`
      placeholder.style.display = 'none'
      originalParent.insertBefore(placeholder, originalVideoContainer)
      
      // 將播放器移動到燈箱
      lightboxVideoContainer.appendChild(originalVideoContainer)
      
      // 調整播放器尺寸以適應燈箱（延遲執行確保 DOM 穩定）
      setTimeout(() => {
        if (videoPlayer.value) {
          try {
            // 獲取燈箱容器尺寸
            const containerRect = lightboxVideoContainer.getBoundingClientRect()
            
            // 強制設定播放器尺寸以填滿燈箱容器
            videoPlayer.value.resize(containerRect.width, containerRect.height)
            
            // 額外設定 CSS 尺寸確保完全填滿
            const playerElement = lightboxVideoContainer.querySelector('.xgplayer')
            const videoElement = lightboxVideoContainer.querySelector('.xgplayer-video')
            
            if (playerElement) {
              playerElement.style.width = '100%'
              playerElement.style.height = '100%'
              playerElement.style.maxWidth = 'none'
              playerElement.style.maxHeight = 'none'
            }
            
            if (videoElement) {
              videoElement.style.width = '100%'
              videoElement.style.height = '100%'
              videoElement.style.objectFit = 'contain'
            }
            
            // 觸發播放器重新布局
            if (videoPlayer.value.emit) {
              videoPlayer.value.emit('resize')
            }
            
          } catch (error) {
            console.warn('調整播放器尺寸時發生錯誤:', error)
          }
        }
      }, 150)
      
      // 為燈箱影片添加進入動畫
      lightboxVideoContainer.classList.add('zoom-in')
      lightboxVideoContainer.classList.remove('zoom-out')
      
      // 儲存還原資訊
      originalVideoContainer.dataset.originalParent = originalParent.id || 'temp-parent'
      originalVideoContainer.dataset.placeholderId = placeholder.id
    }
    
    setTimeout(() => {
      isVideoLightboxAnimating.value = false
    }, 100)
  })
}

const closeVideoLightbox = () => {
  isVideoLightboxAnimating.value = true
  
  const lightboxVideoContainer = document.getElementById(`lightbox-mse-${props.post.id}`)
  const originalVideoContainer = document.getElementById(`mse-${props.post.id}`)
  
  // 暫停小紅書影片播放器
  if (videoPlayer.value) {
    try {
      videoPlayer.value.pause()
    } catch (error) {
      console.warn('暫停影片時發生錯誤:', error)
    }
  }
  
  // 為燈箱影片添加退出動畫
  if (lightboxVideoContainer) {
    lightboxVideoContainer.classList.add('zoom-out')
    lightboxVideoContainer.classList.remove('zoom-in')
  }
  
  setTimeout(() => {
    // 將播放器還原到原始位置
    if (originalVideoContainer && lightboxVideoContainer.contains(originalVideoContainer)) {
      const placeholderId = originalVideoContainer.dataset.placeholderId
      const placeholder = document.getElementById(placeholderId)
      
      if (placeholder && placeholder.parentElement) {
        // 將播放器放回原位置
        placeholder.parentElement.insertBefore(originalVideoContainer, placeholder)
        // 移除占位元素
        placeholder.remove()
      }
      
      // 清理資料屬性
      delete originalVideoContainer.dataset.originalParent
      delete originalVideoContainer.dataset.placeholderId
    }
    
    isVideoLightboxOpen.value = false
    isVideoLightboxAnimating.value = false
    document.body.style.overflow = '' // 恢復滾動
    
    // 清理燈箱動畫類別
    if (lightboxVideoContainer) {
      lightboxVideoContainer.classList.remove('zoom-out')
    }
  }, 300)
}

// YouTube 燈箱相關方法
const openYoutubeLightbox = () => {
  isYoutubeLightboxOpen.value = true
  isYoutubeLightboxAnimating.value = true
  document.body.style.overflow = 'hidden' // 防止背景滾動
  
  nextTick(() => {
    // 為燈箱 YouTube 影片添加進入動畫
    const youtubeLightboxContainer = document.getElementById(`youtube-lightbox-${props.post.id}`)
    if (youtubeLightboxContainer) {
      youtubeLightboxContainer.classList.add('zoom-in')
      youtubeLightboxContainer.classList.remove('zoom-out')
    }
    
    setTimeout(() => {
      isYoutubeLightboxAnimating.value = false
    }, 100)
  })
}

const closeYoutubeLightbox = () => {
  isYoutubeLightboxAnimating.value = true
  
  const youtubeLightboxContainer = document.getElementById(`youtube-lightbox-${props.post.id}`)
  
  // 為燈箱 YouTube 影片添加退出動畫
  if (youtubeLightboxContainer) {
    youtubeLightboxContainer.classList.add('zoom-out')
    youtubeLightboxContainer.classList.remove('zoom-in')
    
    // 暫停 YouTube 影片（透過移除和重新載入 iframe）
    const iframe = youtubeLightboxContainer.querySelector('iframe')
    if (iframe) {
      // 暫停播放的方法：重新設置 src 來停止播放
      const currentSrc = iframe.src
      iframe.src = ''
      // 稍後恢復 src，但因為燈箱即將關閉，實際上不會重新播放
      setTimeout(() => {
        if (iframe.parentNode) {
          iframe.src = currentSrc
        }
      }, 50)
    }
  }
  
  setTimeout(() => {
    isYoutubeLightboxOpen.value = false
    isYoutubeLightboxAnimating.value = false
    document.body.style.overflow = '' // 恢復滾動
    
    // 清理動畫類別
    if (youtubeLightboxContainer) {
      youtubeLightboxContainer.classList.remove('zoom-out')
    }
  }, 300)
}

// 初始化小紅書影片播放器
const initRedbookPlayer = () => {
  if (props.post.source === 'redbook') {
    nextTick(() => {
      const element = document.getElementById(`mse-${props.post.id}`)
      if (element && !videoPlayer.value) {
        try {
          videoPlayer.value = new Player({
            id: `mse-${props.post.id}`,
            url: props.post.video,
            playsinline: true,
            whitelist: [""],
            fluid: true,
            poster: getImageUrl(props.post.image),
            autoplay: false
          })
          console.log(`小紅書影片播放器初始化成功: ${props.post.id}`)
        } catch (error) {
          console.warn(`小紅書影片播放器初始化失敗 (${props.post.id}):`, error.message)
        }
      }
    })
  }
}

// 監聴 post 變化，重新初始化播放器
watch(() => props.post, () => {
  // 清理舊的播放器
  if (videoPlayer.value) {
    try {
      videoPlayer.value.destroy()
      videoPlayer.value = null
    } catch (error) {
      console.warn('清理舊影片播放器時發生錯誤:', error)
    }
  }
  // 初始化新的播放器
  initRedbookPlayer()
}, { deep: true })

// 生命週期
onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
  // 初始化小紅書影片播放器
  initRedbookPlayer()
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = '' // 確保恢復滾動
  
  // 清理影片播放器
  if (videoPlayer.value) {
    try {
      videoPlayer.value.destroy()
      videoPlayer.value = null
    } catch (error) {
      console.warn('清理影片播放器時發生錯誤:', error)
    }
  }
})
</script>

<style scoped>
/* 木質色彩系統 */
.text-wood-50 { color: #faf8f3; }
.text-wood-100 { color: #f4f0e6; }
.text-wood-200 { color: #e8dcc0; }
.text-wood-300 { color: #dcc89a; }
.text-wood-400 { color: #c4a674; }
.text-wood-500 { color: #a68b5b; }
.text-wood-600 { color: #8b7355; }
.text-wood-700 { color: #6d5a47; }
.text-wood-800 { color: #4a3f35; }
.text-wood-900 { color: #2d261f; }

.bg-wood-50 { background-color: #faf8f3; }
.bg-wood-100 { background-color: #f4f0e6; }
.bg-wood-200 { background-color: #e8dcc0; }
.bg-wood-300 { background-color: #dcc89a; }
.bg-wood-400 { background-color: #c4a674; }
.bg-wood-500 { background-color: #a68b5b; }
.bg-wood-600 { background-color: #8b7355; }
.bg-wood-700 { background-color: #6d5a47; }
.bg-wood-800 { background-color: #4a3f35; }
.bg-wood-900 { background-color: #2d261f; }

.border-wood-200 { border-color: #e8dcc0; }
.border-wood-300 { border-color: #dcc89a; }
.border-wood-500 { border-color: #a68b5b; }

/* 大地色彩系統 */
.text-earth-50 { color: #f7f6f4; }
.text-earth-100 { color: #efede8; }
.text-earth-200 { color: #ddd9d0; }
.text-earth-300 { color: #c7c0b3; }
.text-earth-400 { color: #ada296; }
.text-earth-500 { color: #9a8a7a; }
.text-earth-600 { color: #857662; }
.text-earth-700 { color: #6f6252; }
.text-earth-800 { color: #5a5144; }
.text-earth-900 { color: #47423a; }

.bg-earth-50 { background-color: #f7f6f4; }
.bg-earth-100 { background-color: #efede8; }
.bg-earth-200 { background-color: #ddd9d0; }
.bg-earth-300 { background-color: #c7c0b3; }
.bg-earth-400 { background-color: #ada296; }
.bg-earth-500 { background-color: #9a8a7a; }
.bg-earth-600 { background-color: #857662; }
.bg-earth-700 { background-color: #6f6252; }
.bg-earth-800 { background-color: #5a5144; }
.bg-earth-900 { background-color: #47423a; }

/* YouTube 嵌入樣式 */
.embed-container {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  max-width: 100%;
}

.embed-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* 燈箱特效 */
.lightbox-image {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  will-change: transform, opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
}

/* 燈箱動畫 */
@keyframes lightboxZoomIn {
  0% {
    opacity: 0;
    transform: scale(0.3) rotate(-10deg);
    filter: blur(4px);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05) rotate(-2deg);
    filter: blur(1px);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    filter: blur(0px);
  }
}

@keyframes lightboxZoomOut {
  0% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    filter: blur(0px);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.1) rotate(5deg);
    filter: blur(2px);
  }
  100% {
    opacity: 0;
    transform: scale(0.2) rotate(15deg);
    filter: blur(8px);
  }
}

.lightbox-image.zoom-in {
  animation: lightboxZoomIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.lightbox-image.zoom-out {
  animation: lightboxZoomOut 0.4s cubic-bezier(0.55, 0.055, 0.675, 0.19) forwards;
}

/* 按鈕懸停效果增強 */
.lightbox-button {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.lightbox-button:hover {
  transform: scale(1.1);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
}

.lightbox-button:active {
  transform: scale(0.95);
}

/* 圖片切換動畫 */
@keyframes slideOutLeft {
  0% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateX(-100%) scale(0.8);
    opacity: 0;
  }
}

@keyframes slideInLeft {
  0% {
    transform: translateX(100%) scale(0.95);
    opacity: 0;
  }
  50% {
    transform: translateX(0) scale(1.02);
    opacity: 0.8;
  }
  100% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
}

@keyframes slideOutRight {
  0% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateX(100%) scale(0.8);
    opacity: 0;
  }
}

@keyframes slideInRight {
  0% {
    transform: translateX(-100%) scale(0.95);
    opacity: 0;
  }
  50% {
    transform: translateX(0) scale(1.02);
    opacity: 0.8;
  }
  100% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
}

.lightbox-image.slide-left {
  animation: slideOutLeft 0.3s cubic-bezier(0.4, 0, 1, 1) forwards;
}

.lightbox-image.slide-right {
  animation: slideOutRight 0.3s cubic-bezier(0.4, 0, 1, 1) forwards;
}

.lightbox-image.slide-in-left {
  animation: slideInLeft 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

.lightbox-image.slide-in-right {
  animation: slideInRight 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
}

/* 影片燈箱特效 */
.lightbox-video {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  will-change: transform, opacity;
  min-width: 320px;
  min-height: 180px;
}

/* 確保小紅書影片播放器在燈箱中正確顯示 */
.lightbox-video .xgplayer {
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  max-height: none !important;
}

.lightbox-video .xgplayer-video {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
}

/* 影片燈箱動畫 */
@keyframes videoLightboxZoomIn {
  0% {
    opacity: 0;
    transform: scale(0.5) rotate(-5deg);
    filter: blur(3px);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.02) rotate(-1deg);
    filter: blur(1px);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    filter: blur(0px);
  }
}

@keyframes videoLightboxZoomOut {
  0% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    filter: blur(0px);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.05) rotate(3deg);
    filter: blur(2px);
  }
  100% {
    opacity: 0;
    transform: scale(0.3) rotate(10deg);
    filter: blur(5px);
  }
}

.lightbox-video.zoom-in {
  animation: videoLightboxZoomIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.lightbox-video.zoom-out {
  animation: videoLightboxZoomOut 0.3s cubic-bezier(0.55, 0.055, 0.675, 0.19) forwards;
}

/* YouTube 燈箱特效 */
.lightbox-youtube {
  transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  will-change: transform, opacity;
  min-width: 320px;
  min-height: 180px;
}

/* YouTube 燈箱動畫 */
@keyframes youtubeLightboxZoomIn {
  0% {
    opacity: 0;
    transform: scale(0.5) rotate(-3deg);
    filter: blur(2px);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.02) rotate(-0.5deg);
    filter: blur(0.5px);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    filter: blur(0px);
  }
}

@keyframes youtubeLightboxZoomOut {
  0% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
    filter: blur(0px);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.05) rotate(2deg);
    filter: blur(1px);
  }
  100% {
    opacity: 0;
    transform: scale(0.3) rotate(8deg);
    filter: blur(4px);
  }
}

.lightbox-youtube.zoom-in {
  animation: youtubeLightboxZoomIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.lightbox-youtube.zoom-out {
  animation: youtubeLightboxZoomOut 0.3s cubic-bezier(0.55, 0.055, 0.675, 0.19) forwards;
}
</style>